import { memo, useMemo, type JSX, type ReactNode } from 'react';
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
  type FieldValues,
} from 'react-hook-form';
import {
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  FieldInfoButton,
  FormField,
  FormGrid,
  FormSection,
  CheckboxRow,
} from '@/components/blocks/forms';
import { statusToneClasses, statusToneInteractiveFieldClasses } from '@/lib/statusTone';
import type { SafetyFieldConfig, SafetyFormSection } from '../config/schemas/types';

export interface SafetySubTabFormProps {
  /** Field definitions including fields with custom renderers */
  fields: readonly SafetyFieldConfig[];
  /** Form sections with titles and field keys */
  sections: readonly SafetyFormSection[];
  /** Whether the form is disabled */
  disabled?: boolean;
  /**
   * Custom renderers for specific field keys.
   * Use for fields that need special components (CatalogSuggestTagsInput, etc.)
   * These will be rendered inline at the correct position within their section.
   */
  customRenderers?: Record<string, () => ReactNode>;
  /**
   * Optional children for additional manual sections.
   * Rendered after all schema-driven sections.
   */
  children?: ReactNode;
}

/**
 * Schema-driven form component for safety sub-tabs.
 *
 * Renders form fields from schema definitions with support for custom renderers.
 * Custom renderers are inserted inline at the correct position within sections,
 * guaranteeing alignment with the corresponding view cards.
 *
 * @example
 * ```tsx
 * <SafetySubTabForm
 *   fields={rilevazioneFields}
 *   sections={rilevazioneFormSections}
 *   customRenderers={{
 *     detectorPositions: () => <DetectorPositionsField />,
 *   }}
 * />
 * ```
 */
export function SafetySubTabForm({
  fields,
  sections,
  disabled = false,
  customRenderers = {},
  children,
}: SafetySubTabFormProps): JSX.Element {
  const fieldMap = useMemo(() => new Map(fields.map((field) => [field.key, field])), [fields]);

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <FormSection
          key={section.title ?? section.fields.join('-')}
          title={section.title}
          description={section.description}
        >
          <FormGrid>
            {section.fields.map((key) => {
              // Check for custom renderer first
              if (customRenderers[key]) {
                return (
                  <div key={key} className="md:col-span-2">
                    {customRenderers[key]()}
                  </div>
                );
              }

              const field = fieldMap.get(key);
              if (!field) return null;

              return <SafetyFormField key={field.key} field={field} disabled={disabled} />;
            })}
          </FormGrid>
        </FormSection>
      ))}
      {children}
    </div>
  );
}

/**
 * Individual form field renderer for safety forms.
 * Supports text, number, textarea, checkbox, and select types.
 */
const SafetyFormField = memo(function SafetyFormField({
  field,
  disabled,
}: {
  field: SafetyFieldConfig;
  disabled: boolean;
}) {
  const { register, control, getValues } = useFormContext<FieldValues>();
  const { errors } = useFormState({ name: field.key });
  const fieldError = (errors as Record<string, { message?: string }>)[
    field.key
  ]?.message?.toString();
  const dependencyKeys = field.dependsOn ?? [];
  useWatch({ name: dependencyKeys });

  const isRequired = field.required;
  const isDisabled =
    typeof field.disabled === 'function'
      ? Boolean(field.disabled(getValues()))
      : Boolean(field.disabled);
  const resolvedDisabled = isDisabled || disabled;
  const errorTone = statusToneClasses('danger');
  const errorInputClasses = fieldError ? cn(...statusToneInteractiveFieldClasses('danger')) : '';
  const reducedShadowClasses = 'shadow-none hover:shadow-none focus:shadow-none';
  const colSpan = field.type === 'textarea' ? 2 : (field.colSpan ?? 1);

  const labelNode = field.tooltip ? (
    <FieldInfoButton content={field.tooltip} label={field.label} />
  ) : (
    field.label
  );

  if (field.type === 'checkbox') {
    return (
      <div className={cn('space-y-1', colSpan === 2 ? 'md:col-span-2' : null)}>
        <CheckboxRow
          id={field.key}
          label={labelNode}
          description={field.description}
          required={isRequired}
        >
          <Controller
            control={control}
            name={field.key}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                id={field.key}
                checked={Boolean(value)}
                onCheckedChange={onChange}
                disabled={resolvedDisabled}
              />
            )}
          />
        </CheckboxRow>
        {fieldError ? (
          <p className={cn('text-sm', errorTone.text)} role="alert">
            {fieldError}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <FormField
      label={labelNode}
      htmlFor={field.key}
      required={isRequired}
      description={field.description}
      error={fieldError}
      colSpan={colSpan}
    >
      {field.type === 'textarea' ? (
        <Textarea
          id={field.key}
          {...register(field.key)}
          placeholder={field.placeholder}
          disabled={resolvedDisabled}
          className={cn(errorInputClasses, reducedShadowClasses, 'resize-none')}
          rows={4}
        />
      ) : field.type === 'select' ? (
        <Controller
          control={control}
          name={field.key}
          render={({ field: { value, onChange } }) => (
            <Select
              onValueChange={onChange}
              value={value ? String(value) : undefined}
              disabled={resolvedDisabled}
            >
              <SelectTrigger id={field.key} className={cn(errorInputClasses, reducedShadowClasses)}>
                <SelectValue placeholder={field.placeholder ?? "Seleziona un'opzione"} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      ) : (
        <Input
          id={field.key}
          type={field.type === 'number' ? 'number' : 'text'}
          {...register(field.key, {
            valueAsNumber: field.type === 'number',
          })}
          placeholder={field.placeholder}
          disabled={resolvedDisabled}
          className={cn(errorInputClasses, reducedShadowClasses)}
        />
      )}
    </FormField>
  );
});
SafetyFormField.displayName = 'SafetyFormField';
